import { Link } from 'react-router-dom'
import {
  ChevronRight,
  CreditCard,
  HelpCircle,
  History,
  Star,
  MessageSquare,
  LineChart,
  LogOut,
  FlaskConical,
  Lock,
  ShieldCheck,
  Ticket,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useHome } from '@/hooks/useHome'
import { useViewSettings } from '@/hooks/useViewSettings'
import { displayPlanLabel } from '@/lib/utils'

function MenuRow({
  to,
  icon: Icon,
  label,
  sublabel,
  badge,
  iconColorClass = 'text-brand',
  bgColorClass = 'bg-brand-soft/40',
}: {
  to: string
  icon: typeof CreditCard
  label: string
  sublabel?: string
  badge?: string
  iconColorClass?: string
  bgColorClass?: string
}) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between px-4 py-3.5 transition-all hover:bg-panel-elevated/40 active:bg-panel-elevated"
    >
      <span className="flex items-center gap-3.5 text-sm font-bold text-text">
        <span className={`h-8 w-8 rounded-xl ${bgColorClass} flex items-center justify-center shrink-0`}>
          <Icon className={`h-4 w-4 ${iconColorClass}`} />
        </span>
        <span className="flex flex-col">
          <span>{label}</span>
          {sublabel && <span className="text-[10px] font-bold text-text-dim/80">{sublabel}</span>}
        </span>
      </span>
      <span className="flex items-center gap-2">
        {badge && (
          <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-brand-soft text-brand border border-brand/20">
            {badge}
          </span>
        )}
        <ChevronRight className="h-4 w-4 text-text-dim" />
      </span>
    </Link>
  )
}

export function MorePage() {
  const { user, logout } = useAuth()
  const { data: homeData } = useHome(true)
  const { favorites } = useViewSettings()

  const isAdmin = user?.role === 'ADMIN'
  // P9: nunca comparar el nombre del plan a mano (`plan === 'BLACK'`) -- el backend ya
  // resuelve el mapeo central de planes (`app/domain/plan_tiers.py`) en `plan_features`.
  // P0.53 (Sección 19/25 del encargo): mismo flag ya usado por Reto/Banca -- "Mi Evolución"
  // es contenido PREMIUM (PRO/BLACK/ADMIN), nunca un permiso nuevo inventado en frontend.
  const hasPremiumContent = isAdmin || Boolean(homeData?.plan_features?.bankroll)
  // P0.56 (Sección 16 del encargo): misma señal real que DesktopNav/JALabPage --
  // `personal_intelligence` ya resuelve exclusivamente BLACK/ADMIN en `plan_tiers.py`.
  const isBlackUser = isAdmin || Boolean(homeData?.plan_features?.personal_intelligence)

  // P0.55 (Sección 6/31 del encargo): nunca mostrar el rol interno ADMIN como plan comercial.
  const userPlan = displayPlanLabel(homeData?.subscription?.plan || user?.plan)
  const renewalDate = homeData?.subscription?.expires_at 
    ? new Date(homeData.subscription.expires_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'No expira'

  return (
    <div className="p-4 space-y-6">
      
      {/* Perfil del Usuario Premium */}
      <div className="relative overflow-hidden card border-brand/20 bg-gradient-to-b from-panel-elevated/40 to-panel/80 p-5 flex items-center gap-4">
        {/* Glow de fondo */}
        <div className="absolute -right-8 -bottom-8 h-20 w-20 rounded-full bg-brand/10 blur-xl pointer-events-none" />
        
        {/* Avatar Circular con Borde Verde */}
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-brand/30 blur-[2px]" />
          <div className="relative h-14 w-14 rounded-full border-2 border-brand bg-panel flex items-center justify-center font-display text-xl font-black text-brand uppercase shrink-0 shadow-brand-sm select-none">
            {user?.telegram_username ? user.telegram_username.substring(0, 2) : 'JA'}
          </div>
        </div>

        {/* Datos de Usuario */}
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-extrabold text-text truncate">
              {homeData?.display_name || user?.display_name || 'Mi Perfil'}
            </h2>
          </div>
          <p className="text-[10px] font-mono text-text-dim truncate">
            ID: {user?.telegram_user_id || '@Jhon_sportbot'}
          </p>
          <div className="flex items-center gap-2 pt-0.5">
            <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-brand-soft text-brand border border-brand/20">
              Plan {userPlan}
            </span>
            <span className="text-[9px] font-bold text-text-dim">
              Vence: {renewalDate}
            </span>
          </div>
        </div>
      </div>

      {/* SECCIÓN CLIENTE */}
      <div className="space-y-2">
        <p className="text-[10px] font-black text-text-dim uppercase tracking-wider pl-1">Menú de Usuario</p>
        <div className="card p-0 divide-y divide-border/60 overflow-hidden">
          <MenuRow
            to="/quiniela"
            icon={Ticket}
            label="La Quiniela"
            sublabel="15 partidos analizados por John Anthony"
          />
          <MenuRow
            to="/mas/suscripcion"
            icon={CreditCard}
            label="Mi suscripción"
            sublabel="Ver planes, renovación y pagos"
          />
          {/* P0.53 (Sección 11 del encargo): "Historial" unificado -- pestañas
              "Recomendaciones" (oficial) y "Mis apuestas" (personal), en vez de dos
              pantallas separadas en dos sitios distintos. */}
          <MenuRow
            to="/mas/historial"
            icon={History}
            label="Historial"
            sublabel="Qué recomendó John Anthony y qué jugaste tú"
          />
          <MenuRow
            to="/mas/favoritos"
            icon={Star}
            label="Picks favoritos"
            sublabel="Tus apuestas guardadas localmente"
            badge={favorites.length > 0 ? String(favorites.length) : undefined}
          />
          {/* P0.23 (Sección 2 del encargo): "Mi Banca" y "Reto" se promovieron a la barra
              inferior (Inicio/Picks/Parlays/Reto/Banca del mockup maestro) -- ya no viven
              aquí para no duplicar la navegación. */}
          {/* P0.53 (Sección 19/21 del encargo): "Mi Evolución" -- reemplaza al viejo acceso
              directo "Comparador de Estrategias" (ahora una pestaña dentro de esta pantalla).
              Contenido PREMIUM: FREE ve el candado/upsell, nunca la pantalla vacía. */}
          <MenuRow
            to="/mas/evolucion"
            icon={LineChart}
            label="Mi Evolución"
            sublabel={hasPremiumContent ? 'Tu rendimiento real + comparador oficial' : 'Disponible en PRO y BLACK'}
            iconColorClass="text-brand"
            bgColorClass="bg-brand-soft/40"
          />
        </div>
      </div>

      {/* P0.56 (Sección 5/16 del encargo): JA Lab visible también desde Telegram/móvil --
          nunca solo un privilegio de escritorio. FREE/PRO lo ven con candado + badge BLACK
          (descubrimiento), BLACK/ADMIN entra a la foundation real. */}
      <div className="space-y-2">
        <p className="text-[10px] font-black text-exact/70 uppercase tracking-wider pl-1">Black</p>
        <div className="card p-0 divide-y divide-border/60 overflow-hidden border-exact/20">
          <MenuRow
            to="/ja-lab"
            icon={isBlackUser ? FlaskConical : Lock}
            label="John Anthony Lab"
            sublabel={isBlackUser ? 'Analiza el partido que elijas' : 'Descubre la capacidad exclusiva BLACK'}
            iconColorClass="text-exact"
            bgColorClass="bg-exact-soft/30"
            badge={isBlackUser ? undefined : 'BLACK'}
          />
        </div>
      </div>

      {/* Sprint "Corrección Final Product Experience V2" (10/09/2026, Sección 7 del
          encargo): "Administración" -- SOLO ADMIN, nunca visible para un plan comercial
          BLACK real (ADMIN es un ROL, no un plan -- Sección 1 del encargo). Ocultar el
          enlace es solo conveniencia de UX; la barrera de seguridad REAL es `require_
          admin_role` en cada endpoint (`app/api/mini_app.py`), probada con un usuario no-
          ADMIN real (ver reporte final). */}
      {isAdmin && (
        <div className="space-y-2">
          <p className="text-[10px] font-black text-exact/70 uppercase tracking-wider pl-1">Administración</p>
          <div className="card p-0 divide-y divide-border/60 overflow-hidden border-exact/20">
            <MenuRow
              to="/mas/admin"
              icon={ShieldCheck}
              label="Administración"
              sublabel="Usuarios, planes y estado de cuentas"
              iconColorClass="text-exact"
              bgColorClass="bg-exact-soft/30"
            />
          </div>
        </div>
      )}

      {/* CONFIGURACIÓN Y SOPORTE */}
      <div className="space-y-2">
        <p className="text-[10px] font-black text-text-dim uppercase tracking-wider pl-1">Ajustes & Soporte</p>
        <div className="card p-0 divide-y divide-border/60 overflow-hidden">
          {/* P0.26 (Sección 17 del encargo): "Modo de visualización" (Simple/Experta) se
              elimina definitivamente -- existe una sola experiencia John Anthony. */}
          <MenuRow
            to="/mas/ayuda"
            icon={HelpCircle}
            label="Cómo funciona"
            sublabel="Qué es John Anthony, cómo analiza, cómo interpretar cada métrica"
            iconColorClass="text-text-dim"
            bgColorClass="bg-panel-elevated"
          />
          <a
            href="https://t.me/Jhon_sportbot"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between px-4 py-3.5 transition-all hover:bg-panel-elevated/40 active:bg-panel-elevated"
          >
            <span className="flex items-center gap-3.5 text-sm font-bold text-text">
              <span className="h-8 w-8 rounded-xl bg-brand-soft/40 flex items-center justify-center shrink-0">
                <MessageSquare className="h-4 w-4 text-brand" />
              </span>
              <span className="flex flex-col">
                <span>Soporte técnico</span>
                <span className="text-[10px] font-bold text-text-dim/80">Contactar directamente vía Telegram</span>
              </span>
            </span>
            <ChevronRight className="h-4 w-4 text-text-dim" />
          </a>
        </div>
      </div>

      {/* P0.53 (29/08/2026, BLOCK A, Sección 5 del encargo): la sección de administración
          se retira por completo de Client Web -- aunque el usuario sea ADMIN, al entrar aquí
          experimenta el producto cliente. Gestión de usuarios/pagos/banca de sistema/motores
          en producción ya viven en el Dashboard administrativo (`frontend/`), nunca
          duplicados en esta app. */}

      {/* P0.54 (29/08/2026, Sección 15 del encargo): cerrar sesión -- solo tiene efecto
          duradero fuera de Telegram (revoca la sesión de navegador); dentro de Telegram
          WebApp, `initData` fresco vuelve a autenticar automáticamente en la próxima apertura
          (comportamiento esperado, ver docstring de `useAuth`). Nunca borra cuenta/plan/
          banca/apuestas -- solo cierra la sesión actual. */}
      <div className="space-y-2">
        <button
          onClick={() => logout()}
          className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-border/60 text-text-dim hover:text-danger hover:border-danger/40 text-[10px] font-black uppercase tracking-wider transition"
        >
          <LogOut className="h-3.5 w-3.5" /> Cerrar sesión
        </button>
      </div>

      {/* Versión footer de Mini App */}
      <div className="text-center pt-2">
        <p className="text-[9px] font-mono text-text-dim">John Anthony IA · v0.1.0 (PROD)</p>
      </div>

    </div>
  )
}
